import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { ArticleModel } from '@modules/article/models/article.model';
import { ArticleAdapter } from '@modules/content-api/adapter/article.adapter';
import { ParsedEntryInterface } from '@modules/content-api/api.interface';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import { isUUID } from 'src/helpers/uuid';

export interface ArticleResultError {
  type: 'NOT_FOUND' | 'HTTP_ERROR' | 'NO_TRANSLATION';
}

export interface ArticleResult {
  data: ArticleModel | null;
  error: ArticleResultError | null;
}

export const ARTICLE_PAGE_PARAMS = new HttpParams().appendAll({
  include: [
    'header_image.image',
    'header_image.adjusted_image',
    'header_image.alternate_images.image',
    'header_image.alternate_images.adjusted_image',
    'press',
    'images.image',
    'images.adjusted_image',
    'documents.file',
    'videos.video',
    'videos.poster_image',
    'audios.audio',
    'topic_tree',
    'technical_data.file',
  ].join(),
  'fields[media--alternate_images]': 'image',
});

@Injectable({
  providedIn: 'root',
})
export class ArticlePageService {
  private apiService = inject(ContentAPIService);
  private articleAdapter = inject(ArticleAdapter);

  private mapError(error: HttpErrorResponse): ArticleResult {
    const status = error.status;
    if (status === 404) {
      return {
        data: null,
        error: {
          type: 'NOT_FOUND',
        },
      };
    }

    if (status === 406) {
      return {
        data: null,
        error: {
          type: 'NO_TRANSLATION',
        },
      };
    }

    return {
      data: null,
      error: {
        type: 'HTTP_ERROR',
      },
    };
  }

  public getArticle(slugOrId: string) {
    if (isUUID(slugOrId)) {
      return this.apiService.getResource('articles', slugOrId, ARTICLE_PAGE_PARAMS).pipe(
        map((article) => {
          if (!article) {
            return {
              data: null,
              error: {
                type: 'NO_TRANSLATION',
              },
            };
          }

          return {
            data: new ArticleModel(this.articleAdapter.parse(article)),
            error: null,
          };
        }),
        catchError((error) => of(this.mapError(error)))
      );
    }
    // if the article is requested via slug, we have to use a filtered list request
    return this.apiService
      .getResources('articles', ARTICLE_PAGE_PARAMS.append('filter[slug.value]', `/article/${slugOrId}`))
      .pipe(
        map((data: ParsedEntryInterface[]) => {
          if (!data.length) {
            return {
              data: null,
              error: {
                type: 'NO_TRANSLATION',
              },
            };
          }

          return {
            data: new ArticleModel(this.articleAdapter.parse(data[0])) ?? null,
            error: null,
          };
        }),
        catchError((error) => of(this.mapError(error))),
        shareReplay(1)
      );
  }
}
